/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, FontWeight, HStack, ICON_TRASH_CAN_OUTLINE, Text, UserPreview,
  SimpleTable,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import {
  Intent, JobApplication, Note, NoteRole, Property, Prospect, Rumor,
} from '../../../providers/api/dtos';
import {
  ReferenceEntityWithNotes, ReferenceWithNotes, UserRole,
} from '../../../types';
import { ViewAgentSmallPreview } from '../../../agent/components';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import UpsertNoteAction from '../upsert-note-action-v2/UpsertNoteAction';
import { ReferenceType } from '../../../domain/types';
import TextPreview from '../../../components/text-preview/TextPreview';

export interface ViewNoteTableRowBaseProps {
  reference: Property | Intent | Rumor | Prospect | JobApplication,
  referenceType: ReferenceType,
  note: Note,
  canEdit?: boolean,
  canDelete?: boolean,
  onNoteUpdate?: (updatedReference: ReferenceEntityWithNotes) => void,
  onDeleteClick?: (note: Note) => VoidFunction,
}

export type ViewNoteTableRowProps = ViewNoteTableRowBaseProps & ReferenceWithNotes;

export const NoteAuthor: React.FC<Pick<ViewNoteTableRowProps, 'note'>> = ({ note }) => {
  if (note.role === UserRole.CUSTOMER) {
    return (
      <UserPreview
        name="Contatto"
        size="XS"
        aria-label="Preview del contatto"
      />
    );
  }

  if (([UserRole.AGENT, UserRole.AREA_MANAGER] as NoteRole[]).includes(note.role)) {
    // @TODO: Enrich preview with agent data
    return (
      <ViewAgentSmallPreview
        name={note.author}
        size="XS"
        aria-label="Preview dell'agente"
      />
    );
  }

  return (
    <UserPreview
      name={note.author}
      size="XS"
      aria-label="Preview del'autore"
    />
  );
};

const ViewNoteTableRow: React.FC<ViewNoteTableRowProps> = ({
  reference,
  referenceType,
  note,
  canEdit = true,
  canDelete = true,
  onNoteUpdate,
  onDeleteClick, // @TODO: Create un componente DeleteNoteAction e utilizzarlo qui (rimuovendo l'implementazione su NotesWidget)
}) => {
  const {
    date, author, role,
  } = note;
  const { userIsAdmin, mainUserRole, user } = useRBAC();

  const showNoteActions = React.useMemo(() => (canEdit || canDelete) && ((author === user.name && role === mainUserRole) || userIsAdmin),
    [canEdit, canDelete, author, user.name, role, mainUserRole, userIsAdmin]);

  return (
    <SimpleTable.Row>
      <SimpleTable.Cell width="8rem">
        <NoteAuthor note={note} />
      </SimpleTable.Cell>
      <SimpleTable.Cell>
        <TextPreview
          text={note.text}
          author={note.author}
          date={note.date}
        />
      </SimpleTable.Cell>
      <SimpleTable.Cell>
        <Text.Caption fontWeight={FontWeight.MEDIUM}>
          {formatDate(new Date(date!))}
        </Text.Caption>
      </SimpleTable.Cell>
      {showNoteActions && (
        <SimpleTable.Cell align="right">
          <HStack
            gap={75}
            aria-label="Azioni delle note"
            wrap="nowrap"
          >
            {canEdit && (
              <UpsertNoteAction
                reference={reference}
                referenceType={referenceType}
                note={note}
                onSuccess={onNoteUpdate}
              />
            )}
            {canDelete && (
              <ActionIcon
                label="Elimina"
                icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                onClick={onDeleteClick?.(note)}
                size="S"
                aria-label="Pulsante per eliminare una nota"
              />
            )}
          </HStack>
        </SimpleTable.Cell>
      )}
    </SimpleTable.Row>
  );
};

export default ViewNoteTableRow;
